import { useCallback, useMemo, useRef, useState } from 'react';
import { IconCopy, IconCrop, IconRotateClockwise, IconTrash } from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import ImageCropDialog from './ImageCropDialog';
import { cropImageFile, duplicateImageFile, rotateImageFile } from './imageFileEdits';

/**
 * Crop / rotate / duplicate / delete for a tool's picked images.
 *
 * Every image tool keeps its files differently — a plain array, a list of
 * `{ id, file }` rows, a single file — so the hook asks for the three things it
 * cannot guess: how to replace a file, how to add one, how to drop one. What it
 * gives back is a ready-made action list (for `ImageFileRow` and
 * `ImageGridPreview`) plus the crop dialog to render.
 *
 * The edits rewrite the SOURCE file, so a tool's own pipeline needs to know
 * nothing about them: it just processes whatever files it now has.
 *
 * @param {{
 *   files: File[],
 *   onReplace: (index: number, file: File) => void,
 *   onInsert?: (index: number, file: File) => void,
 *   onRemove?: (index: number) => void,
 * }} config
 */
export default function useImageFileEdits({ files, onReplace, onInsert, onRemove }) {
    const { t } = useTranslation();
    const [cropIndex, setCropIndex] = useState(null);
    const [busyIndex, setBusyIndex] = useState(null);
    // A crop replaces the file, so cropping twice used to cut a cut-out: the
    // dialog reopened on the already-trimmed picture with a fresh, centred
    // selection. Each cropped file therefore remembers what it was cut FROM and
    // where — reopening shows the original with the last rectangle still on it,
    // and saving re-cuts the original rather than stacking crops. Rotating (or
    // any other edit) starts a new lineage, since the picture itself changed.
    const cropStateRef = useRef(new WeakMap());

    const fail = useCallback(() => {
        notifications.show({
            title: t('common.error'),
            message: t('imageEdits.failed'),
            color: 'red',
        });
    }, [t]);

    const run = useCallback(async (index, work) => {
        const file = files[index];
        if (!file) return;
        setBusyIndex(index);
        try {
            onReplace(index, await work(file));
        } catch {
            fail();
        } finally {
            setBusyIndex(null);
        }
    }, [files, onReplace, fail]);

    const actions = useMemo(() => {
        const list = [
            {
                id: 'crop',
                icon: IconCrop,
                label: t('cropImage.actions.crop'),
                onClick: (index) => setCropIndex(index),
            },
            {
                id: 'rotate',
                icon: IconRotateClockwise,
                label: t('cropImage.actions.rotate'),
                onClick: (index) => run(index, (file) => rotateImageFile(file, 1)),
            },
        ];

        if (onInsert) {
            list.push({
                id: 'duplicate',
                icon: IconCopy,
                label: t('common.duplicate'),
                onClick: (index) => {
                    const file = files[index];
                    if (!file) return;
                    const copy = duplicateImageFile(file, files.map((f) => f.name));
                    // The copy is the same picture, so it inherits where that
                    // picture was cut from — its crop dialog opens like the
                    // original's, not on a blank selection.
                    const entry = cropStateRef.current.get(file);
                    if (entry) cropStateRef.current.set(copy, entry);
                    onInsert(index, copy);
                },
            });
        }
        if (onRemove) {
            list.push({
                id: 'delete',
                icon: IconTrash,
                label: t('common.delete'),
                onClick: (index) => onRemove(index),
            });
        }
        return list.map((action) => ({ ...action, disabled: busyIndex != null }));
    }, [t, run, files, onInsert, onRemove, busyIndex]);

    const cropEntry = cropIndex != null ? cropStateRef.current.get(files[cropIndex]) : undefined;

    const cropDialog = cropIndex != null && files[cropIndex] ? (
        <ImageCropDialog
            file={cropEntry?.source ?? files[cropIndex]}
            savedCrop={cropEntry?.cropPct}
            onClose={() => setCropIndex(null)}
            onSave={(cropPct) => {
                const index = cropIndex;
                setCropIndex(null);
                const source = cropStateRef.current.get(files[index])?.source ?? files[index];
                void run(index, async () => {
                    const cropped = await cropImageFile(source, cropPct);
                    cropStateRef.current.set(cropped, { source, cropPct });
                    return cropped;
                });
            }}
        />
    ) : null;

    return { actions, cropDialog, busyIndex };
}
